import React, { useEffect, useState } from "react";
import { X, Printer, Plus, Loader2, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { rupiah, fmtDateTime, fmtDate } from "@/lib/format";
import { STATUS_LABEL, STATUS_BADGE, PAYMENT_LABEL, PAYMENT_BADGE } from "@/lib/constants";
import { Button, Badge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

export default function OrderDetailModal({ orderId, transitions = {}, onClose, onChanged }) {
  const { can, isOwner } = useAuth();
  const [order, setOrder] = useState(null);
  const [methods, setMethods] = useState([]);
  const [busy, setBusy] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [showPay, setShowPay] = useState(false);

  const load = async () => {
    const [o, m] = await Promise.all([
      api.get(`/orders/${orderId}`),
      api.get("/payment-methods").catch(() => []),
    ]);
    setOrder(o);
    setMethods(m);
    if (m[0]) setPayMethod(m[0].code || m[0].id || "cash");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const changeStatus = async (status) => {
    let reason = "";
    if (status === "cancelled" || status === "failed_delivery") {
      reason = window.prompt("Alasan?") || "";
      if (!reason.trim()) return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/status`, { status, reason });
      toast.success(`Status → ${STATUS_LABEL[status]}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const qcDecision = async (result) => {
    let failure_reason = "";
    if (result === "fail") {
      failure_reason = window.prompt("Alasan QC gagal?") || "";
      if (!failure_reason.trim()) return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/qc`, { result, failure_reason, checklist: {} });
      toast.success(result === "pass" ? "QC Lolos" : "QC Gagal — revisi");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addPayment = async () => {
    const amount = Number(payAmount);
    if (!amount || amount <= 0) return toast.error("Jumlah tidak valid");
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/payments`, { method: payMethod, amount });
      toast.success("Pembayaran dicatat");
      setShowPay(false); setPayAmount("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const printInvoice = () => window.print();

  const deleteOrder = async () => {
    if (!window.confirm(`Hapus PERMANEN order ${order.order_no}?\n\nOmset, komisi & piutang order ini akan hilang dari semua laporan. Tindakan ini tidak bisa dibatalkan.`)) return;
    setBusy(true);
    try {
      await api.del(`/orders/${orderId}`);
      toast.success(`Order ${order.order_no} dihapus`);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const nextStatuses = order ? (transitions[order.status] || []) : [];
  const canApprove = can("orders", "approve") || isOwner;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        {!order ? (
          <div className="flex items-center justify-center h-full text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-semibold text-slate-800">{order.order_no}</div>
                <div className="text-xs text-slate-400">{fmtDateTime(order.created_at)} · {order.branch_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={printInvoice}><Printer size={15} /> Cetak</Button>
                <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={18} /></button>
              </div>
            </div>

            <div id="print-area" className="p-6 space-y-5">
              <div className="flex flex-wrap gap-2">
                <Badge className={STATUS_BADGE[order.status]}>{order.status_label || STATUS_LABEL[order.status]}</Badge>
                <Badge className={PAYMENT_BADGE[order.payment_status]}>{PAYMENT_LABEL[order.payment_status]}</Badge>
                <Badge className="bg-slate-100 text-slate-600">{order.fulfillment === "delivery" ? "Dikirim" : "Diambil"}</Badge>
                {order.is_overdue && <Badge className="bg-rose-100 text-rose-700">Terlambat</Badge>}
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Field label="Pelanggan" value={order.customer_name || "-"} />
                <Field label="Telepon" value={order.customer_phone || "-"} />
                <Field label="Tanggal Ambil/Kirim" value={order.due_date ? fmtDate(order.due_date) : "-"} />
                <Field label="Perangkai" value={(order.florist_names || []).join(", ") || "-"} />
                {order.delivery_address && <div className="col-span-2"><Field label="Alamat" value={order.delivery_address} /></div>}
                {order.greeting_card && <div className="col-span-2"><Field label="Kartu Ucapan" value={order.greeting_card} /></div>}
              </div>

              {(order.request_photo_file_id || order.wrap_photo_file_id) && (
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Lampiran Foto</div>
                  <div className="flex flex-wrap gap-4">
                    {order.request_photo_file_id && (
                      <a href={fileUrl(order.request_photo_file_id)} target="_blank" rel="noreferrer" className="block" data-testid="order-request-photo">
                        <span className="text-xs text-slate-400 mb-1 block">Request Order</span>
                        <img src={fileUrl(order.request_photo_file_id)} alt="Request order" className="w-32 h-32 rounded-xl object-cover border border-slate-200" />
                      </a>
                    )}
                    {order.wrap_photo_file_id && (
                      <a href={fileUrl(order.wrap_photo_file_id)} target="_blank" rel="noreferrer" className="block" data-testid="order-wrap-photo">
                        <span className="text-xs text-slate-400 mb-1 block">Warna Bungkus</span>
                        <img src={fileUrl(order.wrap_photo_file_id)} alt="Warna bungkus" className="w-32 h-32 rounded-xl object-cover border border-slate-200" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Item</div>
                <div className="border border-slate-100 rounded-xl divide-y divide-slate-100">
                  {order.items.map((it, i) => (
                    <div key={i} className="flex justify-between px-4 py-2.5 text-sm">
                      <span className="text-slate-700">{it.qty}× {it.name}</span>
                      <span className="font-medium">{rupiah(it.subtotal)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-sm">
                <Row label="Subtotal" value={rupiah(order.subtotal)} />
                {order.discount > 0 && <Row label="Diskon" value={`- ${rupiah(order.discount)}`} />}
                {order.delivery_fee > 0 && <Row label="Ongkir" value={rupiah(order.delivery_fee)} />}
                <Row label="Total" value={rupiah(order.total)} bold />
                <Row label="Dibayar" value={rupiah(order.paid_amount)} />
                {order.balance_due > 0 && <Row label="Sisa" value={rupiah(order.balance_due)} tone="text-rose-600" bold />}
              </div>

              {(order.payments || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Pembayaran</div>
                  {order.payments.map((p) => (
                    <div key={p.id} className="flex justify-between text-sm py-1">
                      <span className="text-slate-500">{p.method} · {fmtDateTime(p.created_at)}</span>
                      <span className="font-medium">{rupiah(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 space-y-3 print:hidden">
              {order.balance_due > 0 && can("pos", "create") && (
                showPay ? (
                  <div className="flex gap-2">
                    <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="h-10 px-2 rounded-xl border border-slate-300 text-sm">
                      {methods.map((m) => <option key={m.id || m.code} value={m.code || m.id}>{m.name}</option>)}
                      {!methods.length && <option value="cash">Tunai</option>}
                    </select>
                    <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder={`Maks ${order.balance_due}`} className="flex-1 h-10 px-3 rounded-xl border border-slate-300 text-sm" />
                    <Button size="sm" onClick={addPayment} disabled={busy}>Simpan</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowPay(false)}>Batal</Button>
                  </div>
                ) : (
                  <Button variant="subtle" onClick={() => { setShowPay(true); setPayAmount(String(order.balance_due)); }} className="w-full"><Plus size={16} /> Tambah Pembayaran</Button>
                )
              )}

              {order.status === "qc" && (
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={() => qcDecision("pass")} disabled={busy}><CheckCircle2 size={16} /> QC Lolos</Button>
                  <Button variant="danger" className="flex-1" onClick={() => qcDecision("fail")} disabled={busy}><XCircle size={16} /> QC Gagal</Button>
                </div>
              )}

              {nextStatuses.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {nextStatuses.filter((s) => s !== "ready" || order.status !== "qc").map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={s === "cancelled" ? "danger" : "outline"}
                      onClick={() => changeStatus(s)}
                      disabled={busy || (s === "cancelled" && !canApprove)}
                    >
                      {STATUS_LABEL[s]}
                    </Button>
                  ))}
                </div>
              )}

              {isOwner && (
                <button
                  onClick={deleteOrder}
                  disabled={busy}
                  data-testid="delete-order-btn"
                  className="w-full flex items-center justify-center gap-2 h-10 rounded-xl text-rose-600 hover:bg-rose-50 text-sm font-medium border border-rose-200"
                >
                  <Trash2 size={16} /> Hapus Order Permanen
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-400">{label}</div>
    <div className="text-slate-700">{value}</div>
  </div>
);
const Row = ({ label, value, bold, tone }) => (
  <div className="flex justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={`${bold ? "font-semibold" : ""} ${tone || "text-slate-800"}`}>{value}</span>
  </div>
);
